import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { VersionReportDoc } from "@/types/domain";

// Shared atoms of the three report-doc panels (PRD cell / engagement cell / version
// timeline). Each panel keeps its own layout shell and composes these.

/** External link to the report doc, truncating; layout via className. */
export const ReportDocLink = ({
  doc,
  className,
}: {
  doc: VersionReportDoc;
  className?: string;
}) => (
  <Link
    to={doc.url}
    target="_blank"
    rel="noreferrer"
    className={cn("truncate text-sm underline hover:text-primary", className)}
  >
    {doc.name || "Report"}
  </Link>
);

/** Inline summary editor: textarea + Cancel / Save. */
export const ReportSummaryEditor = ({
  value,
  onChange,
  onCancel,
  onSave,
  isSaving,
}: {
  value: string;
  onChange: (value: string) => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving?: boolean;
}) => (
  <div className="space-y-2">
    <Textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="min-h-[100px]"
      placeholder="Enter a summary…"
    />
    <div className="flex justify-end gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onCancel();
        }}
        disabled={isSaving}
      >
        Cancel
      </Button>
      <Button
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onSave();
        }}
        disabled={isSaving}
      >
        {isSaving ? "Saving…" : "Save"}
      </Button>
    </div>
  </div>
);

/** Delete confirmation dialog. Pass `children` to render an inline trigger;
 * omit it for a single dialog controlled from row menus. Closes only after a
 * successful delete (the confirm handler keeps it open while mutating). */
export const ReportDeleteDialog = ({
  open,
  onCancel,
  onConfirm,
  isMutating,
  children,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  isMutating?: boolean;
  children?: ReactNode;
}) => (
  <AlertDialog
    open={open}
    onOpenChange={(o) => {
      if (!o && !isMutating) onCancel();
    }}
  >
    {children && <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>}
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Confirm delete</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to delete this report? This action cannot be
          undone.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel disabled={isMutating}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onConfirm();
          }}
          disabled={isMutating}
          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
        >
          {isMutating ? "Deleting…" : "Delete"}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
